import { AlignOption, get_operators_for_type } from "target_wasm";
import {
  DISPLAY_ENTRY,
  DISPLAY_ENTRY_FOR_COLUMN,
  type CatalogKey,
} from "@/actions/catalog";
import {
  canChangeSchema,
  canCastColumn,
  canDelete,
  canEditIntents,
  canExtractUnique,
  canLink,
  canManageCategories,
  canMapToReference,
  canOpenDescription,
  canSearchReplace,
  canSetDataType,
  canSetUnit,
  canSplit,
  canToggleLock,
  displayFormatTarget,
} from "@/components/canvas/detailsSide/tableDetails/ColumnList/columnActionGates";
import {
  extractUnique,
  lockColumnType,
  requestDeleteColumn,
} from "@/components/canvas/detailsSide/tableDetails/ColumnList/columnOps";
import type { OverlayInitialView } from "@/components/canvas/detailsSide/tableDetails/ColumnList/entities";
import { ImportSourceType } from "@/components/canvas/leftSide/import/ImportSourceType";
import { useLeftPanelStore } from "@/components/canvas/leftSide/useLeftPanelStore";
import { openAddToDataset } from "@/components/canvas/openers/addToDataset";
import {
  openCheckpoints,
  syncToLatestRelease,
} from "@/components/canvas/openers/checkpoints";
import {
  exportItem,
  openExportSheets,
} from "@/components/canvas/openers/exportItem";
import { useBoundStore } from "@/components/canvas/stores/useAppStore";
import { useCheckpointStore } from "@/components/canvas/stores/CheckpointStore";
import {
  cellOperator,
  useFilterStore,
  type FilterColumn,
  type FilterPeek,
} from "@/components/canvas/stores/FilterStore";
import { copyTableId } from "@/components/canvas/utils/copyTableId";
import { deleteTables } from "@/components/canvas/trash/deleteTables";
import { useColumnDescriptionStore } from "stores/useColumnDescriptionStore";
import { useCheckpointsStore } from "stores/useCheckpointsStore";
import { openColumnProperties } from "stores/useColumnPropertiesStore";
import { useLinkCreationStore } from "stores/useLinkCreationStore";
import { useLinkedColumnsStore } from "stores/useLinkedColumnsStore";
import { useModelShareStore } from "stores/useModelShareStore";
import { useMoveTableStore } from "stores/useMoveTableStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { useReferenceMatchingStore } from "stores/useReferenceMatchingStore";
import { useSearchReplaceStore } from "stores/useSearchReplaceStore";
import { useSplitColumnStore } from "stores/useSplitColumnStore";
import { useTableShareStore } from "stores/useTableShareStore";
import { openAttachmentViewer } from "@/components/canvas/attachments/viewer/useAttachmentViewerStore";
import { VIEW_FILE_SHORTCUT } from "@/components/canvas/attachments/cellFiles";
import {
  isUnitBearingKind,
  onColumn,
  schemaOpen,
  type BarContext,
} from "./conditions";
import type { FamilyId } from "./families";
import type { OverlayKey, OverlayOpens } from "./overlays";
import type { SelectionState } from "./selection";

export type Opens = "run" | "dialog" | "menu";

/** What a slot's colour says: edits ready to commit, or a filter in force. */
export type Accent = "commit" | "filter";

/** One row of a menu member: what it wears and what a pick does. */
export interface Choice {
  key: string;
  entry: CatalogKey;
  label?: string;
  run: (x: BarContext) => void;
}

/** An action, whole: where it is offered, when it applies, what it wears and
 *  what a press does. Every surface reads this, nothing restates it. */
export type Action<Id extends string = ActionId> = {
  family: FamilyId;
  /** Its place in its family: the lowest that reverses nothing is the family's
   *  icon on the bar, and every other member a row of ⋮. */
  order: number;
  /** The selections that offer it. */
  on: readonly SelectionState[];
  when: (x: BarContext) => boolean;
  entry: CatalogKey | ((x: BarContext) => CatalogKey);
  /** Never an icon of its own. */
  reverses?: Id;
  /** Never its family's icon: it waits in ⋮, as a reversal does. */
  menuOnly?: true;
  /** Its name in the shortcut sheet, so its tooltip names the keys. */
  shortcut?: string;
  /** While ⌥ (Alt) is held, the slot wears this instead and a click runs it. */
  alt?: Id;
  /** Sits last, after a divider. */
  destructive?: true;
  /** The slot's colour while this is its default. */
  accent?: (x: BarContext) => Accent | null;
  /** The overlay it opens: while that is up for this target, its slot is
   *  pressed and a press closes it. */
  overlay?: {
    [K in OverlayKey]: {
      key: K;
      showing: (open: NonNullable<OverlayOpens[K]>, x: BarContext) => boolean;
    };
  }[OverlayKey];
} & (
  | {
      opens: "run" | "dialog";
      run: (x: BarContext, anchor?: HTMLElement) => void;
    }
  | { opens: "menu"; heading?: string; choices: (x: BarContext) => Choice[] }
);

const app = () => useBoundStore.getState().clientApp;

/** Says what failed instead of leaving the press silent. */
const tryOr = (message: string, act: () => void) => {
  try {
    act();
  } catch (e) {
    showNotificationGlobal(message || String(e), "error");
  }
};

const cancelCommit = ({ target: { tableId } }: BarContext) => {
  if (tableId) tryOr("", () => app()?.cancel_commit(tableId));
};

/** A view's filter: its editors, on the table canvas, over values they can read. */
const filterable = (x: BarContext) =>
  x.canEditModel && x.valuesReadable && !!x.target.viewId && !x.inGraph;
/** The column a Filter by… names, once its view can be filtered on it. */
const filterColumn = (x: BarContext): FilterColumn | null =>
  filterable(x) && x.target.columnId && x.column && !x.column.isDeletedColumn
    ? {
        id: x.target.columnId,
        name: x.target.columnName ?? "",
        dataType: x.column.dataType,
      }
    : null;
const viewFilter = (x: BarContext) =>
  app()?.get_table_view_details(x.target.viewId!)?.filter;
/** Filter by this column, or with a cell's value by that value. */
const filterBy = (
  x: BarContext,
  anchor: HTMLElement | undefined,
  cellValue?: string | null,
) => {
  const column = filterColumn(x);
  if (column)
    tryOr("", () =>
      useFilterStore
        .getState()
        .openFilterFor(x.target.viewId!, viewFilter(x), column, {
          anchor: slotAnchor(x, anchor),
          cellValue,
        }),
    );
};
const FILTER_OVERLAY = {
  key: "filter",
  showing: (open: { viewId: string }, x: BarContext) =>
    open.viewId === x.target.viewId,
} as const;
export type FilterAction = "filter-view" | "filter-col" | "filter-cell";
/** A Filter's editor: the view's filter, or its column's or value's conditions. */
const editFilter = (id: FilterAction, x: BarContext, anchor?: HTMLElement) => {
  if (id !== "filter-view")
    return filterBy(x, anchor, id === "filter-cell" ? x.cellValue : undefined);
  const { viewId } = x.target;
  if (viewId)
    tryOr("", () =>
      useFilterStore
        .getState()
        .openFilterDialog(viewId, viewFilter(x), slotAnchor(x, anchor)),
    );
};
/** The view's filter read above a Filter's slot; a column's or cell's lights its row. */
export const filterPeek = (
  id: FilterAction,
  x: BarContext,
  anchor: HTMLElement,
): FilterPeek => ({
  viewId: x.target.viewId!,
  anchor,
  columnId: id === "filter-view" ? undefined : x.target.columnId,
  cellValue: id === "filter-cell" ? x.cellValue : undefined,
  edit: () => editFilter(id, x, anchor),
});
/** Lit, a Filter's press reads the filter in force and only the read view's
 *  Edit edits it; unlit, or from the pane, it edits. */
const pressFilter =
  (id: FilterAction, lit: (x: BarContext) => boolean) =>
  (x: BarContext, anchor?: HTMLElement) => {
    const slot = slotAnchor(x, anchor);
    if (slot && lit(x))
      useFilterStore
        .getState()
        .setPeek({ ...filterPeek(id, x, slot), pinned: true });
    else editFilter(id, x, anchor);
  };

const TABLE: readonly SelectionState[] = ["single-tview"];
const TABLES: readonly SelectionState[] = ["single-tview", "multi-tview"];
const COLUMN: readonly SelectionState[] = ["single-col"];
const COLUMN_OR_CELL: readonly SelectionState[] = ["single-col", "cell"];
/** A cell, column or row of one view: editing there is what a commit stages. */
const IN_VIEW: readonly SelectionState[] = [
  "single-tview",
  "single-col",
  "multi-col",
  "single-row",
  "multi-row",
  "cell",
];

/** The bar keeps commit mode in reach while you edit; a column's ⋮ stays the column's. */
const inViewCommit = (x: BarContext) =>
  x.canEditModel &&
  (x.target.surface === "bar" || (x.target.columnIds?.length ?? 0) === 0);

/** Anchored above the bar's slot; beside the pane from the pane. */
const slotAnchor = (x: BarContext, anchor?: HTMLElement) =>
  x.target.surface === "bar" ? anchor : undefined;

const sameColumn = (
  open: { tableId: string; columnId: string },
  { target }: BarContext,
) => open.tableId === target.tableId && open.columnId === target.columnId;

/** Set type, Display format, Cast, Unit and Intents: one overlay, a view each. */
const properties = (view: (x: BarContext) => OverlayInitialView | null) => ({
  run: (x: BarContext, anchor?: HTMLElement) => {
    const { tableId, viewId, columnId } = x.target;
    const v = view(x);
    if (!tableId || !columnId || !v) return;
    // Beside the pane, a column overlay takes the history's place.
    if (x.target.surface === "pane") useCheckpointsStore.getState().close();
    openColumnProperties({
      tableId,
      viewId,
      columnId,
      view: v,
      anchor: slotAnchor(x, anchor),
    });
  },
  overlay: {
    key: "properties" as const,
    showing: (open: NonNullable<OverlayOpens["properties"]>, x: BarContext) =>
      sameColumn(open, x) && open.view === view(x),
  },
});

/** Search & replace, Split and Map to reference: a dialog on the column. */
const columnDialog =
  (store: typeof useSearchReplaceStore) =>
  ({ target: { tableId, tableName, columnId, columnName } }: BarContext) => {
    if (tableId && columnId)
      store
        .getState()
        .show({ tableId, tableName: tableName ?? "", columnId, columnName });
  };

// Beside a column means beside it in a view.
const besideColumn = (x: BarContext) =>
  !!x.target.viewId && onColumn(canChangeSchema)(x);

const addBeside =
  (left: boolean) =>
  ({ target: { viewId, columnId } }: BarContext) => {
    if (viewId && columnId)
      tryOr("Could not add the column", () =>
        app()?.add_column_beside(viewId, columnId, left),
      );
  };

// Moving tables acts on the canvas, not on an item, so no ⋮ offers it; nor does a graph,
// which does not show tables where they sit.
const movesViews = (x: BarContext) =>
  x.target.surface === "bar" && x.canEditModel && !x.inGraph;

/** Align and Distribute: one run each, offered once `views` views are selected. */
const aligning = (views: number, option: AlignOption) => ({
  on: TABLES,
  when: (x: BarContext) => movesViews(x) && x.viewCount >= views,
  opens: "run" as const,
  run: () =>
    tryOr("Could not align the tables", () => app()?.align_view_items(option)),
});

const displayTarget = (x: BarContext) =>
  x.column ? displayFormatTarget(x.column) : null;

const IMPORT_ROWS: readonly [ImportSourceType, CatalogKey, string?][] = [
  [ImportSourceType.Upload, "importFile", "File"],
  [ImportSourceType.Mycorr, "importMycorr"],
  [ImportSourceType.Connections, "importConnection"],
];

const SPECS = {
  "view-file": {
    family: "view",
    order: 10,
    on: ["cell"],
    when: (x) => x.cellFiles.length > 0,
    entry: "viewFile",
    shortcut: VIEW_FILE_SHORTCUT,
    opens: "run",
    run: ({ target: { tableId }, cellFiles }) => {
      if (tableId && cellFiles.length > 0)
        openAttachmentViewer({ tableId, files: [...cellFiles], index: 0 });
    },
  },

  // Only an Owned table's cells take edits to stage.
  "start-commit": {
    family: "commit",
    order: 0,
    on: IN_VIEW,
    when: (x) =>
      inViewCommit(x) && x.schemaEditable && x.commitState === "pauseAuto",
    entry: "startCommit",
    opens: "run",
    run: ({ target: { viewId } }) => {
      if (viewId) tryOr("", () => app()?.start_commit(viewId));
    },
  },
  commit: {
    family: "commit",
    order: 1,
    on: IN_VIEW,
    when: (x) => inViewCommit(x) && x.commitState === "commit",
    entry: "commit",
    // The same whether or not it asks: the question says what changes.
    accent: () => "commit",
    // The question hangs from ↑, so a second press takes it back, as ✕'s does.
    overlay: {
      key: "typeChange",
      showing: (open, x) => open.view_id === x.target.viewId,
    },
    opens: "run",
    run: ({ target: { viewId } }) => {
      if (viewId) tryOr("", () => app()?.press_commit(viewId));
    },
  },
  // A conflict can only be discarded; an empty commit has nothing to discard.
  "discard-edits": {
    family: "discard",
    order: 0,
    on: IN_VIEW,
    when: (x) =>
      inViewCommit(x) &&
      (x.commitState === "commit" || x.commitState === "conflict"),
    entry: "discardEdits",
    destructive: true,
    // Undo brings a discarded commit back, so nothing asks first.
    opens: "run",
    run: cancelCommit,
  },
  "leave-commit": {
    family: "discard",
    order: 1,
    on: IN_VIEW,
    when: (x) => inViewCommit(x) && x.commitState === "empty",
    entry: "leaveCommit",
    opens: "run",
    run: cancelCommit,
  },
  // An import pins a release; a newer one waits until someone here takes it.
  "sync-release": {
    family: "version",
    order: 10,
    on: TABLE,
    when: (x) => x.canEditModel && x.newRelease,
    entry: "syncRelease",
    opens: "run",
    run: ({ target: { tableId } }) => {
      if (tableId) syncToLatestRelease(tableId);
    },
  },
  "filter-view": {
    family: "filter",
    order: 0,
    on: TABLE,
    when: filterable,
    entry: (x) => (x.viewFiltered ? "editFilter" : "addFilter"),
    accent: (x) => (x.viewFiltered ? "filter" : null),
    opens: "dialog",
    run: pressFilter("filter-view", (x) => x.viewFiltered),
    overlay: FILTER_OVERLAY,
  },
  // A column's way into its view's filter: on its conditions, or one made for it.
  "filter-col": {
    family: "filter",
    order: 10,
    on: COLUMN,
    when: (x) => {
      const column = filterColumn(x);
      return !!column && get_operators_for_type(column.dataType).length > 0;
    },
    entry: "filterByColumn",
    // Its own column's state, not the view's: the press lands on that condition.
    accent: (x) => (x.columnInFilter ? "filter" : null),
    opens: "dialog",
    run: pressFilter("filter-col", (x) => x.columnInFilter),
    overlay: FILTER_OVERLAY,
  },
  // A cell's way, on the bar since a cell has few actions: the condition
  // holds its value, or Is Null for an empty cell.
  "filter-cell": {
    family: "filter",
    order: 10,
    on: ["cell"],
    when: (x) => {
      const column = filterColumn(x);
      return !!column && cellOperator(column.dataType, x.cellValue) !== null;
    },
    entry: "filterByValue",
    // Lit only where the filter uses this very value, as a pressed state.
    accent: (x) => (x.cellInFilter ? "filter" : null),
    opens: "dialog",
    run: pressFilter("filter-cell", (x) => x.cellInFilter),
    overlay: FILTER_OVERLAY,
  },
  "remove-filter": {
    family: "filter",
    order: 1,
    on: TABLE,
    when: (x) => filterable(x) && x.viewFiltered,
    entry: "removeFilter",
    reverses: "filter-view",
    opens: "run",
    run: ({ target: { tableId, viewId } }) => {
      if (tableId && viewId)
        tryOr("", () => app()?.update_table_view_filter(tableId, viewId, null));
    },
  },
  "open-checkpoints": {
    family: "version",
    order: 40,
    on: TABLE,
    when: (x) => !!x.history && !x.inGraph,
    entry: (x) => (x.history === "Releases" ? "releases" : "checkpoints"),
    opens: "dialog",
    run: ({ target: { tableId } }, anchor) => {
      if (tableId) openCheckpoints(tableId, anchor);
    },
    overlay: {
      key: "checkpoints",
      showing: (open, x) => open.tableId === x.target.tableId,
    },
  },
  // The bar's Checkpoints slot opens the history, where releasing lives.
  "set-release": {
    family: "version",
    order: 46,
    on: TABLE,
    when: (x) =>
      x.target.surface !== "bar" &&
      x.canRelease &&
      !x.inGraph &&
      !x.currentVersionIsRelease,
    entry: "setRelease",
    opens: "run",
    run: ({ target: { tableId, tableVersion } }) => {
      if (tableId && tableVersion !== undefined)
        void useCheckpointStore.getState().setRelease(tableId, tableVersion);
    },
  },
  "clear-release": {
    family: "version",
    order: 48,
    on: TABLE,
    // A release can point at an older version, so both release rows can apply.
    when: (x) =>
      x.target.surface !== "bar" &&
      x.canRelease &&
      !x.inGraph &&
      x.releaseIsSet,
    entry: "clearRelease",
    reverses: "set-release",
    opens: "run",
    run: ({ target: { tableId } }) => {
      if (tableId) void useCheckpointStore.getState().clearRelease(tableId);
    },
  },

  // Made in a graph, the table joins it.
  "new-table": {
    family: "add",
    order: 20,
    on: ["none"],
    when: (x) => x.canEditModel,
    entry: "newTable",
    opens: "run",
    run: () => tryOr("", () => app()?.handle_new_table_ui()),
  },
  "import-table": {
    family: "import",
    order: 25,
    on: ["none"],
    when: (x) => x.importSources.length > 0 && !x.inGraph,
    entry: "importTable",
    opens: "menu",
    heading: "Import from",
    choices: (x) =>
      IMPORT_ROWS.filter(([source]) => x.importSources.includes(source)).map(
        ([source, entry, label]) => ({
          key: source,
          entry,
          label,
          run: () => useLeftPanelStore.getState().openImport(source),
        }),
      ),
  },
  // Once per table, so it waits in ⋮ rather than taking an icon.
  "attach-col": {
    family: "add",
    order: 70,
    menuOnly: true,
    on: TABLE,
    when: (x) => schemaOpen(x) && !x.inGraph && !x.hasAttachmentColumn,
    entry: "attachmentColumn",
    opens: "run",
    run: ({ target: { tableId } }) => {
      if (tableId)
        tryOr("Could not add the column", () =>
          app()?.add_attachment_column(tableId),
        );
    },
  },

  "set-type": {
    family: "type",
    order: 100,
    on: COLUMN,
    when: onColumn(canSetDataType),
    entry: "setDataType",
    opens: "dialog",
    ...properties(() => "setType"),
  },
  // No schema edit: Rust takes a display setting on any table, as the ⋮ offers it.
  "col-display": {
    family: "type",
    order: 110,
    on: COLUMN_OR_CELL,
    when: (x) => !!x.column && x.canEditModel && displayTarget(x) !== null,
    entry: (x) => {
      const target = displayTarget(x) ?? "stringFormat";
      return (x.cell ? DISPLAY_ENTRY_FOR_COLUMN : DISPLAY_ENTRY)[target];
    },
    opens: "dialog",
    ...properties(displayTarget),
  },
  "manage-categories": {
    family: "type",
    order: 115,
    on: COLUMN_OR_CELL,
    when: onColumn(canManageCategories),
    entry: "manageCategories",
    opens: "dialog",
    ...properties(() => "categories"),
  },
  "cast-col": {
    family: "type",
    order: 120,
    on: COLUMN,
    when: onColumn(canCastColumn),
    entry: "castColumn",
    opens: "dialog",
    ...properties(() => "cast"),
  },
  "lock-type": {
    family: "type",
    order: 175,
    on: COLUMN,
    when: onColumn(
      (col) => canToggleLock(col) && !!col.parentTableId && !col.isLockedType,
    ),
    entry: "lockType",
    opens: "run",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId && columnId) lockColumnType(tableId, columnId, true);
    },
  },
  "unlock-type": {
    family: "type",
    order: 177,
    on: COLUMN,
    when: onColumn(
      (col) => canToggleLock(col) && !!col.parentTableId && !!col.isLockedType,
    ),
    entry: "unlockType",
    reverses: "lock-type",
    opens: "run",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId && columnId) lockColumnType(tableId, columnId, false);
    },
  },

  "col-desc": {
    family: "describe",
    order: 180,
    on: COLUMN,
    when: onColumn(canOpenDescription),
    entry: (x) =>
      x.column?.hasDescription ? "editDescription" : "addDescription",
    opens: "dialog",
    run: (x, anchor) => {
      const { tableId, columnId, columnName, surface } = x.target;
      if (!tableId || !columnId) return;
      useColumnDescriptionStore.getState().show({
        tableId,
        columnId,
        columnName,
        canEdit: true,
        mode: "edit",
        anchor: anchor ?? null,
        side: surface === "bar" ? "above" : undefined,
      });
    },
    overlay: { key: "description", showing: sameColumn },
  },
  "remove-desc": {
    family: "describe",
    order: 182,
    on: COLUMN,
    when: onColumn((col) => canOpenDescription(col) && !!col.hasDescription),
    entry: "removeDescription",
    reverses: "col-desc",
    opens: "run",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId && columnId)
        tryOr("", () => app()?.set_column_description(tableId, columnId, null));
    },
  },
  intents: {
    family: "describe",
    order: 190,
    on: COLUMN,
    when: onColumn(canEditIntents),
    entry: "editIntents",
    opens: "dialog",
    ...properties(() => "intents"),
  },

  "split-col": {
    family: "transform",
    order: 150,
    on: COLUMN,
    when: onColumn(canSplit),
    entry: "splitColumn",
    opens: "dialog",
    run: columnDialog(useSplitColumnStore),
  },
  "map-ref": {
    family: "transform",
    order: 160,
    on: COLUMN,
    when: onColumn(canMapToReference),
    entry: "mapToReference",
    opens: "dialog",
    run: columnDialog(useReferenceMatchingStore),
  },
  // Ahead of Extract unique values: a number column's Transform icon.
  unit: {
    family: "transform",
    order: 162,
    on: COLUMN_OR_CELL,
    when: onColumn((col) => canSetUnit(col) && isUnitBearingKind(col.dataType)),
    entry: (x) => (x.cell ? "setUnitForColumn" : "setUnit"),
    opens: "dialog",
    ...properties(() => "unit"),
  },
  "clear-unit": {
    family: "transform",
    order: 163,
    on: COLUMN,
    // Whatever the kind: a legacy unit on a Text column can still be cleared,
    // and so can one on a hidden or deleted column; an open commit refuses it.
    when: onColumn((col, x) => !col.deleteDisabled && x.columnHasUnit),
    entry: "clearUnit",
    reverses: "unit",
    opens: "run",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId && columnId)
        tryOr("Could not clear the unit", () =>
          app()?.set_column_unit_assignment(tableId, columnId, null),
        );
    },
  },
  "extract-unique": {
    family: "transform",
    order: 165,
    on: ["single-col", "multi-col"],
    when: (x) =>
      schemaOpen(x) &&
      (x.column ? canExtractUnique(x.column) : x.multiColumnExtractable),
    entry: "extractUnique",
    opens: "run",
    run: ({ target: { tableId, columnId, columnIds = [] } }) => {
      if (tableId)
        extractUnique(tableId, columnId ? [columnId] : [...columnIds]);
    },
  },

  "table-links": {
    family: "connect",
    order: 85,
    on: TABLE,
    when: (x) => x.linksEditable,
    entry: "tableLinks",
    opens: "dialog",
    run: ({ target: { tableId } }) => {
      if (tableId)
        useLinkCreationStore.getState().show({ firstTableId: tableId });
    },
  },
  "link-col": {
    family: "connect",
    order: 130,
    on: COLUMN,
    when: (x) => x.linksEditable && !!x.column && canLink(x.column),
    entry: "linkColumn",
    opens: "dialog",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId)
        useLinkCreationStore
          .getState()
          .show({ firstTableId: tableId, firstColumnId: columnId });
    },
  },
  // The column's own link list owns the choice among links and the confirm.
  "unlink-col": {
    family: "connect",
    order: 132,
    on: COLUMN,
    when: (x) =>
      x.linksEditable && !!x.column && canLink(x.column) && !!x.column.isLinked,
    entry: "removeLink",
    reverses: "link-col",
    opens: "dialog",
    run: (x, anchor) => {
      const { tableId, columnId } = x.target;
      if (tableId && columnId)
        useLinkedColumnsStore
          .getState()
          .show({ tableId, columnId, anchor: slotAnchor(x, anchor) });
    },
    overlay: { key: "links", showing: sameColumn },
  },

  "add-col-right": {
    family: "addColumn",
    order: 200,
    on: COLUMN,
    when: besideColumn,
    entry: "addColumnRight",
    alt: "add-col-left",
    opens: "run",
    run: addBeside(false),
  },
  "add-col-left": {
    family: "addColumn",
    order: 201,
    on: COLUMN,
    when: besideColumn,
    entry: "addColumnLeft",
    opens: "run",
    run: addBeside(true),
  },

  // With nothing selected it arranges every table; one view alone has nothing to arrange.
  arrange: {
    family: "arrange",
    order: 300,
    on: ["none", ...TABLES],
    when: (x) =>
      movesViews(x) && (x.target.tableIds.length === 0 || x.viewCount >= 2),
    entry: "arrange",
    opens: "run",
    run: () =>
      tryOr("Could not arrange the tables", () => app()?.arrange_view_items()),
  },
  "align-left": {
    family: "alignX",
    order: 310,
    entry: "alignLeft",
    alt: "align-right",
    ...aligning(2, AlignOption.Left),
  },
  "align-right": {
    family: "alignX",
    order: 311,
    entry: "alignRight",
    ...aligning(2, AlignOption.Right),
  },
  // `CenterV` lines the views up on one x-centre.
  "align-centers-x": {
    family: "alignX",
    order: 312,
    entry: "alignHorizontalCenters",
    ...aligning(2, AlignOption.CenterV),
  },
  "align-top": {
    family: "alignY",
    order: 320,
    entry: "alignTop",
    alt: "align-bottom",
    ...aligning(2, AlignOption.Top),
  },
  "align-bottom": {
    family: "alignY",
    order: 321,
    entry: "alignBottom",
    ...aligning(2, AlignOption.Bottom),
  },
  "align-centers-y": {
    family: "alignY",
    order: 322,
    entry: "alignVerticalCenters",
    ...aligning(2, AlignOption.CenterH),
  },
  "distribute-h": {
    family: "distribute",
    order: 330,
    entry: "distributeHorizontally",
    alt: "distribute-v",
    ...aligning(3, AlignOption.DistributeH),
  },
  "distribute-v": {
    family: "distribute",
    order: 331,
    entry: "distributeVertically",
    ...aligning(3, AlignOption.DistributeV),
  },

  "export-view": {
    family: "export",
    order: 50,
    on: TABLE,
    when: (x) => x.exportable,
    entry: "export",
    opens: "menu",
    choices: ({ target: { itemId } }) =>
      itemId
        ? [
            {
              key: "csv",
              entry: "exportCsv",
              run: () => exportItem(itemId, "csv"),
            },
            {
              key: "parquet",
              entry: "exportParquet",
              run: () => exportItem(itemId, "parquet"),
            },
            {
              key: "sheets",
              entry: "exportSheets",
              run: () => openExportSheets(itemId),
            },
          ]
        : [],
  },

  "model-access": {
    family: "share",
    order: 30,
    on: ["none"],
    when: (x) => x.modelAccess,
    entry: "manageAccess",
    opens: "dialog",
    run: () => {
      const clientApp = app();
      if (clientApp)
        useModelShareStore
          .getState()
          .show({ modelId: clientApp.model_id_str() });
    },
  },
  "table-access": {
    family: "share",
    order: 30,
    on: TABLE,
    when: (x) => x.canShareTable,
    entry: "manageAccess",
    opens: "dialog",
    run: ({ target: { tableId, tableName, tableVersion } }) => {
      if (tableId && tableName)
        useTableShareStore
          .getState()
          .show({ tableId, tableName, version: tableVersion ?? 0 });
    },
  },

  "search-replace": {
    family: "more",
    order: 140,
    on: COLUMN,
    when: onColumn(canSearchReplace),
    entry: "searchReplace",
    opens: "dialog",
    run: columnDialog(useSearchReplaceStore),
  },
  "del-col": {
    family: "more",
    order: 900,
    on: COLUMN,
    when: (x) => !!x.column && canDelete(x.column),
    entry: "deleteFromTable",
    destructive: true,
    opens: "run",
    run: ({ target: { tableId, columnId } }) => {
      if (tableId && columnId) requestDeleteColumn(tableId, columnId);
    },
  },
  "add-dataset": {
    family: "more",
    order: 890,
    on: TABLES,
    when: (x) => x.canDataset,
    entry: (x) => (x.inAnyDataset ? "addOrRemoveDataset" : "addToDataset"),
    opens: "dialog",
    run: ({ target: { tableIds } }) => openAddToDataset([...tableIds]),
  },
  // The bar deletes nothing; several tables are deleted from their pane.
  "delete-tables": {
    family: "more",
    order: 900,
    on: ["multi-tview"],
    when: (x) => x.target.surface !== "bar" && x.canTrashTables,
    entry: "deleteSelected",
    destructive: true,
    opens: "run",
    run: ({ target: { tableIds } }) => {
      const clientApp = app();
      const targets = tableIds.flatMap((id) => {
        try {
          const t = clientApp?.table_details_by_table_id(id);
          return t ? [{ id, name: t.table_name, source: t.source }] : [];
        } catch {
          return [];
        }
      });
      void deleteTables(clientApp, targets);
    },
  },
  transfer: {
    family: "more",
    order: 905,
    on: TABLE,
    when: (x) => x.canTransfer,
    entry: "transfer",
    opens: "dialog",
    run: ({ target: { tableId, tableName } }) => {
      if (tableId && tableName)
        useMoveTableStore.getState().show({ tableId, tableName });
    },
  },
  "copy-table-id": {
    family: "more",
    order: 910,
    on: TABLE,
    when: () => true,
    entry: "copyTableId",
    opens: "run",
    run: ({ target: { tableId } }) => {
      if (tableId) copyTableId(tableId);
    },
  },
} as const satisfies Record<string, Action<string>>;

export type ActionId = keyof typeof SPECS;

export const ACTIONS: Readonly<Record<ActionId, Action>> = SPECS;

export const ACTION_IDS = Object.keys(ACTIONS) as ActionId[];

export const applies = (id: ActionId, x: BarContext): boolean =>
  ACTIONS[id].when(x);

export const entryOf = (id: ActionId, x: BarContext): CatalogKey => {
  const { entry } = ACTIONS[id];
  return typeof entry === "function" ? entry(x) : entry;
};

/** The members a selection offers that apply, by `order`. */
export const applicable = (state: SelectionState, x: BarContext): ActionId[] =>
  ACTION_IDS.filter(
    (id) => ACTIONS[id].on.includes(state) && applies(id, x),
  ).sort((a, b) => ACTIONS[a].order - ACTIONS[b].order);
